/**
 * グローバル変数・オブジェクトなど
 */

/** 日付(年月日)の最小値を表す文字列 */
export const DATE_MIN_STRING = "1900-01-01";
/** 日付(年月日)の最大値を表す文字列 */
export const DATE_MAX_STRING = "9999-12-31";

/**
 * ファイル拡張子
 */
export const Extension = {
  PNG: ".png",
  SVG: ".svg",
  GIF: ".gif",
  JPG: ".jpg",
} as const;
export type ExtensionType = (typeof Extension)[keyof typeof Extension];

/** 
 * Content-Type
 */
export const ContentType = {
  applicationJson: "application/json;charset=utf-8",
  excel: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  imageJpeg: "image/jpeg",
  imageGif: "image/gif",
  imagePng: "image/png",
  imageWebp: "image/webp",
  imageHeic: "image/heic",
  imageBmp: "image/bmp",
  imageSvg: "image/svg+xml",
  applicationPdf: "application/pdf",
  formData: "multipart/form-data",
} as const;
export type ContentTypeType = (typeof ContentType)[keyof typeof ContentType];

export const Icon = {
  /* 汎用 */
  Close: "close",
  Question_Black: "question_black",
  ModalClose: "modal_close",

  ArrowForward_Black: "arrow_forward_black",
  AllowBack_Black: "arrow_back_black",
  /* 製品 */
  LogoProductIcon: "moneylink_logo",

  Bank_Black: "account_balance_black",
  UserAccount_Black: "account_circle_black",
  WebPage_Black: "ad_group_black",
  ConnectCloudService_Black: "automation_black",
  LoginHistory_Black: "browse_gallery_black",
  Creca_Black: "credit_card_black",
  ElectronicMoney_Black: "payment_card_black",
  EcShop_Black: "paid_black",
  CSV_Black: "csv_black",
  MyData_White: "data_table_white",
  Delete_Black: "delete_black",
  Delete_White: "delete_white",
  Ocr_Black: "document_scanner_black",
  CreateFolder_Black: "create_new_folder_black",
  Edit_Black: "edit_black",
  EditSquare_Black: "edit_square_black",
  TwoStepAuth_Black: "encrypted_add_black",
  Home_White: "home_white",
  AccountGroup_White: "inventory_2_white",
  AccountGroup_Black: "inventory_2_black",
  Logout_Black: "logout_black",
  Mail_Black: "mail_black",
  News_Black: "newsmode_black",
  Notice_Black: "notifications_black",
  Password_Black: "password_black",
  User_Black: "person_black",
  EditUser_Black: "person_edit_black",
  Tutorial_Black: "play_lesson_black",
  Share_Black: "share_black",
  Help_White: "support_white",
  Help_Black: "support_black",
  MemoUnforcused_Gray: "chat_gray",
  MemoForcused_Orange: "chat_orange",
  Yen_White: "currency_yen_white",
  Download_Black: "download_black",
  Search_White: "search_white",
  Toggle_White: "toggle_off_white",
  /* 口座追加モーダル関連 */
  BankAccount: "bank-account",
  CreditCard: "credit-card",
  ElectronicMoney: "electronic-money",
  EcShop: "ec-shop",
  OcrAccount: "ocr-account",
  ExcelAccount: "excel-account",
  /*OCR明細取込方法選択*/
  // FileAggregate: "file-aggregate",
  // CameraAggregate: "camera-aggregate",
} as const;
export type IconType = (typeof Icon)[keyof typeof Icon];

/** 金融機関タイプ */
export const InstitutionsType = {
  Bank: "bank",
  Card: "card",
  ElectronicMoney: "ev",
  Other: "other", // =ECShop
  Ocr: "ocr",
}
export type InstitutionsTypeType = (typeof InstitutionsType)[keyof typeof InstitutionsType];

/** 検索頭文字 */
export const SearchCode = {
  a: "ア",
  i: "イ",
  u: "ウ",
  e: "エ",
  o: "オ",
  ka: "カ",
  ki: "キ",
  ku: "ク",
  ke: "ケ",
  ko: "コ",
  sa: "サ",
  si: "シ",
  su: "ス",
  se: "セ",
  so: "ソ",
  ta: "タ",
  ti: "チ",
  tu: "ツ",
  te: "テ",
  to: "ト",
  na: "ナ",
  ni: "ニ",
  nu: "ヌ",
  ne: "ネ",
  no: "ノ",
  ha: "ハ",
  hi: "ヒ",
  hu: "フ",
  he: "ヘ",
  ho: "ホ",
  ma: "マ",
  mi: "ミ",
  mu: "ム",
  me: "メ",
  mo: "モ",
  ya: "ヤ",
  yu: "ユ",
  yo: "ヨ",
  ra: "ラ",
  ri: "リ",
  ru: "ル",
  re: "レ",
  ro: "ロ",
  wa: "ワ",
  //wo: "ヲ",
  //n: "ン",
} as const;
export type SearchCodeType = (typeof SearchCode)[keyof typeof SearchCode];

/** 2段階認証
  *   0:無効
  *   1:有効 */
export const TwoStepAuthValid = {
  Invalid: 0,
  Valid: 1,
} as const;
export type TwoStepAuthValidType = (typeof TwoStepAuthValid)[keyof typeof TwoStepAuthValid];

/** 認証タイプ
  *   0:新規登録時認証
  *   1:コンバート後初回認証 */
export const AuthenticationType = {
  SignUpAuth: 0,
  FirstAuth: 1,
} as const;
export type AuthenticationTypeType = (typeof AuthenticationType)[keyof typeof AuthenticationType];

/** OCR読み取り状況()
  *   0:初期状態(待機中)
  *   1:読み取り中
  *   2:正常終了
  *   3:異常終了
  *   5:タイムアウト */
export const OcrProcessStatus = {
  Initial: 0,
  Processing: 1,
  Completed: 2,
  Error: 3,
  Timeout: 5,
} as const;
export type OcrProcessStatusType = (typeof OcrProcessStatus)[keyof typeof OcrProcessStatus];

/* OCRカメラ読み取り切替 */
export const OcrCameraAggregateMode = {
  Shoot: "shoot",
  Detail: "detail",
  Confirm: "confirm",
} as const;
export type OcrCameraAggregateModeType = (typeof OcrCameraAggregateMode)[keyof typeof OcrCameraAggregateMode];

/* OCRカメラ次動作確認 */
export const OcrCameraNextAction = {
  Finish: "finish",
  Resume: "resume",
} as const;
export type OcrCameraNextActionType = (typeof OcrCameraNextAction)[keyof typeof OcrCameraNextAction];

/* メールアドレスドメインの補完リスト */
export const SuggestionDomainNameList: string[] = [
  "@au.com",
  "@docomo.ne.jp",
  "@excite.co.jp",
  "@ezweb.ne.jp",
  "@gmail.com",
  "@googlemail.com",
  "@hotmail.co.jp",
  "@hotmail.com",
  "@i.softbank.jp",
  "@icloud.com",
  "@live.jp",
  "@me.com",
  "@mineo.jp",
  "@nifty.com",
  "@outlook.com",
  "@outlook.jp",
  "@softbank.ne.jp",
  "@yahoo.co.jp",
  "@yahoo.ne.jp",
  "@ybb.ne.jp",
  "@ymobile.ne.jp",
];

/**
 * Api呼び出しのResponse汎用クラス
 *
 * @export
 * @class HttpResponse
 * @template T
 */
export class HttpResponse<T> {
  constructor(
    public status: number = 0,
    public ok = false,
    public contentType = "",
    public location = "",
    public contentLength = 0,
    public headers: Map<string, string> = new Map<string, string>(),
    public body = {} as T,
    public rawBody = "",
    public errorType = "",
    public errorCode = 0,
    public errorMessage = "",
  ) {}

  bodyAsList(): any[] {
    const result: any[] = JSON.parse(this.rawBody);
    return result;
  }

  bodyAsObject(): Map<string, any> {
    const result: Map<string, any> = JSON.parse(this.rawBody);
    return result;
  }
}

/** 検索頭文字 */
export const CsvBankCodeList = [
  { value: "33", label: "PayPay銀行" },
  { value: "5221", label: "京葉銀行[個人]" },
  { value: "5222", label: "京葉銀行[法人]" },
  { value: "5432", label: "名古屋銀行[法人]" },
  { value: "5621", label: "みなと銀行[個人]" },
  { value: "5622", label: "みなと銀行[法人]" },
  { value: "5651", label: "島根銀行[個人]" },
  { value: "9661", label: "JCB法人カード" },
  { value: "9664", label: "ＭＵＦＧカード・ビジネス/AMEX" },
  { value: "9801", label: "Times PAY" },
];
